import i18n from 'i18next';

import LocaleCode from '@app/store/LocaleCode';

const englishOrdinalNumbers = [
  'first',
  'second',
  'third',
  'fourth',
  'fifth',
  'sixth',
  'seventh',
  'eighth',
  'ninth',
  'tenth',
  'eleventh',
  'twelfth',
  'thirteenth',
  'fourteenth',
  'fifteenth',
  'sixteenth',
  'seventeenth',
  'eighteenth',
  'nineteenth',
  'twentieth',
  'twenty-one',
  'twenty-two',
  'twenty-three',
  'twenty-four',
  'twenty-fifth',
  'twenty-sixth',
  'twenty-seventh',
  'twenty-eighth',
  'twenty-ninth',
  'thirtieth',
];

const chineseOrdinalNumbers = [
  '第一',
  '第二',
  '第三',
  '第四',
  '第五',
  '第六次',
  '第七次',
  '第八',
  '第九次',
  '第十次',
  '第十一',
  '第十二届',
  '第十三届',
  '第十四届',
  '第十五届',
  '第十六届',
  '第十七届',
  '第十八届',
  '第十九届',
  '第二十届',
  '第二十一届',
  '第二十二届',
  '第二十三届',
  '第二十四届',
  '第二十五届',
  '第二十六届',
  '第二十七届',
  '第二十八届',
  '第二十九届',
  '第三十届',
];

const japaneseOrdinalNumbers = [
  '最初の',
  '2番目の',
  '3番目の',
  '4番目の',
  '5番目の',
  '6番目の',
  '7番目の',
  '8番目の',
  '9番目の',
  '10番目の',
  '11番目の',
  '12番目の',
  '13番目の',
  '14番目の',
  '15番目の',
  '16番目の',
  '17番目の',
  '18番目の',
  '19番目の',
  '20番目の',
  '21番目の',
  '22番目の',
  '23番目の',
  '24番目の',
  '25番目の',
  '26番目の',
  '27番目の',
  '28番目の',
  '29番目の',
  '30番目の',
];

const vietnameseOrdinalNumbers = [
  'thứ một',
  'thứ hai',
  'thứ ba',
  'thứ tư',
  'thứ năm',
  'thứ sáu',
  'thứ bảy',
  'thứ tám',
  'thứ chín',
  'thứ mười',
  'thứ mười một',
  'thứ mười hai',
  'thứ mười ba',
  'thứ mười bốn',
  'thứ mười lăm',
  'thứ mười sáu',
  'thứ mười bảy',
  'thứ mười tám',
  'thứ mười chín',
  'thứ hai mươi',
  'thứ hai mươi mốt',
  'thứ hai mươi hai',
  'thứ hai mươi ba',
  'thứ hai mươi bốn',
  'thứ hai mươi lăm',
  'thứ hai mươi sáu',
  'thứ hai mươi bảy',
  'thứ hai mươi tám',
  'thứ hai mươi chín',
  'thứ ba mươi',
];

const koreanOrdinalNumbers = [
  '첫',
  '두',
  '세',
  '네',
  '다섯',
  '여섯',
  '일곱',
  '여덟',
  '아홉',
  '열',
  '열한',
  '열두',
  '열세',
  '열네',
  '열다섯',
  '열여섯',
  '열일곱',
  '열여덟',
  '열아홉',
  '스무',
  '스물한',
  '스물두',
  '스물세',
  '스물네',
  '스물다섯',
  '스물여섯',
  '스물일곱',
  '스물여덟',
  '스물아홉',
  '서른',
].map((x) => `${x} 번째`);

const ordinalNumbersMap: Record<LocaleCode, string[]> = {
  en: englishOrdinalNumbers,
  ko: koreanOrdinalNumbers,
  ja: japaneseOrdinalNumbers,
  'zh-CN': chineseOrdinalNumbers,
  vi: vietnameseOrdinalNumbers,
};

export type AllowedOrdinalNumbers =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14
  | 15
  | 16
  | 17
  | 18
  | 19
  | 20
  | 21
  | 22
  | 23
  | 24
  | 25
  | 26
  | 27
  | 28
  | 29
  | 30;

/**
 * 숫자를 기준으로 서수(ordinal number) 글자를 반환한다.
 * @param {number} number - 반환할 1에서 30까지의 숫자
 * @returns {string}
 */
export default function createOrdinalCharacter(number: AllowedOrdinalNumbers) {
  if (!number || typeof number !== 'number') {
    throw new TypeError('매개변수가 숫자가 아닙니다.');
  }

  const ordinalNumbers =
    ordinalNumbersMap[i18n.language.split('-')[0] as LocaleCode] ?? ordinalNumbersMap.ko;

  const formattedString = ordinalNumbers[number - 1];
  if (formattedString === undefined) {
    throw new Error('대응되는 숫자가 존재하지 않습니다.');
  }

  return formattedString;
}
